import React, { useEffect, useState } from "react";
import { Group, Text, UnstyledButton, ActionIcon } from "@mantine/core";
import DataTable from "./dataTable";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";
import moment from "moment";
import { useAuthUser } from "react-auth-kit";
import { useCellRender } from "../hooks/useCellRender";
import { IconRefresh } from "@tabler/icons";

const DebitNoteRelatedDebitNotesSection = ({ form, source = "debitNote" }) => {
  const entityId = form?.values?._id;
  const [rows, setRows] = useState([]);
  const [api] = useServerApi();
  const { t } = useTranslation();
  const auth = useAuthUser();
  const [cellRender] = useCellRender();
  const [debitNoteLinkPath, setDebitNoteLinkPath] = useState("/m/gi/debitNote");

  useEffect(() => {
    setDebitNoteLinkPath(
      auth()?.userRole?.entityLinks?.find((l) => l.entity === "debitNote")
        ?.link ?? "/m/gi/debitNote",
    );
  }, [auth]);

  const fetchRelatedDebitNotes = async () => {
    try {
      if (!entityId) return;
      const data =
        source === "renewal"
          ? await api.Renewal.getRelatedDebitNotes({ id: entityId })
          : await api.DebitNote.getRelatedDebitNotes({ id: entityId });
      setRows(data?.debitNotes ?? []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchRelatedDebitNotes();
  }, [entityId, source]);

  const tableSchema = {
    hidePagination: true,
    hideSearchBar: true,
    columns: [
      {
        field: "code",
        headerName: "DN",
        cellRender: (_, data) =>
          cellRender.DebitNoteCode(data, true, true, debitNoteLinkPath),
      },
      {
        field: "paymentStatus",
        headerName: "Payment",
        cellRender: (_, data) => (
          <>
            {cellRender.DebitNotePaymentStatus(data.paymentStatus)}
            <div>{cellRender.Currency(data?.clientPayable ?? 0)}</div>
          </>
        ),
      },
      {
        field: "policyNumber",
        headerName: "Policy Number",
        cellRender: (_, data) => (
          <div>
            <Text>{data.policyNumber ?? ""}</Text>
            {data.period?.start && data.period?.end && (
              <Text size="xs" color="dimmed">
                {moment(data.period.start).format("YYYY/MM/DD")} -{" "}
                {moment(data.period.end).format("YYYY/MM/DD")}
              </Text>
            )}
          </div>
        ),
      },
      {
        field: "product",
        headerName: "Product",
        cellRender: (_, data) => {
          if (data.product) return cellRender.ProductInfo(data.product);
          if (data.productType || data.provider)
            return (
              <Text color="dimmed">{cellRender.ProductInfo2(data)}</Text>
            );
          return "-";
        },
      },
    ],
  };

  return (
    <>
      <Group position="right" mb="xs">
        <UnstyledButton onClick={() => fetchRelatedDebitNotes()}>
          <Group spacing={2}>
            <ActionIcon size="sm">
              <IconRefresh size={16} />
            </ActionIcon>
            <Text size="xs">{t("Refresh")}</Text>
          </Group>
        </UnstyledButton>
      </Group>
      {_.isEmpty(rows) && (
        <Group mb="xl">
          <Text size="xs">{t("No related debit notes found")}</Text>
        </Group>
      )}
      {!_.isEmpty(rows) && (
        <DataTable
          data={rows}
          columns={tableSchema.columns}
          hidePagination={true}
          hideSearchBar={true}
        />
      )}
    </>
  );
};

export default DebitNoteRelatedDebitNotesSection;
